<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue';
import CounterpartyMasterCard from '@/modules/counterparty/counterparty-master/components/CounterpartyMasterCard.vue';
import type { CounterpartyAddressItem } from '@/modules/counterparty/counterparty-master/types/address-form.ts';
import {
  getAddressCardLayout,
  measureTextLineCount,
} from '@/modules/counterparty/counterparty-master/utils/address-card-layout.util.ts';
import UiTooltip from '@/modules/ui/components/ui/UiTooltip.vue';

const { item } = defineProps<{
  item: CounterpartyAddressItem;
}>();

defineEmits<{
  edit: [];
  remove: [];
}>();

const bodyRef = useTemplateRef('body');
const addressRef = useTemplateRef('address');
const noteRef = useTemplateRef('note');
const addressLineCount = ref(4);
const addressTooltipDisabled = ref(true);
const noteTooltipDisabled = ref(true);

const hasNote = computed(() => !!item.comment.trim());
const layout = computed(() => getAddressCardLayout(addressLineCount.value, hasNote.value));

let resizeObserver: ResizeObserver | null = null;

function isClamped(el: HTMLElement | null) {
  return !!el && el.scrollHeight > el.clientHeight + 1;
}

function syncAddressTooltip() {
  addressTooltipDisabled.value = !isClamped(addressRef.value);
}

function syncNoteTooltip() {
  noteTooltipDisabled.value = !isClamped(noteRef.value);
}

async function updateLineCount() {
  await nextTick();

  if (!addressRef.value) {
    return;
  }

  addressLineCount.value = measureTextLineCount(addressRef.value, item.address);
}

onMounted(() => {
  void updateLineCount();

  if (typeof ResizeObserver === 'undefined' || !bodyRef.value) {
    return;
  }

  resizeObserver = new ResizeObserver(() => void updateLineCount());
  resizeObserver.observe(bodyRef.value);
});

onUnmounted(() => {
  resizeObserver?.disconnect();
});

watch(
  () => [item.address, item.comment] as const,
  () => void updateLineCount()
);
</script>

<template>
  <CounterpartyMasterCard
    class="address-card"
    @edit="$emit('edit')"
    @remove="$emit('remove')"
  >
    <template #title>{{ item.type.label }}</template>

    <div
      ref="body"
      class="address-body _flush"
    >
      <p
        v-if="layout.showAddressHeader"
        class="meta"
      >
        {{ $t('counterparty.master.steps.address.card.address') }}:
      </p>

      <UiTooltip
        class="clamp"
        :text="item.address"
        :disabled="addressTooltipDisabled"
        placement="bottom"
        align="left"
        @pointerenter="syncAddressTooltip"
      >
        <p
          ref="address"
          class="text address"
          title=""
        >
          {{ item.address }}
        </p>
      </UiTooltip>

      <template v-if="hasNote">
        <p class="meta">{{ $t('counterparty.master.steps.address.card.note') }}:</p>
        <UiTooltip
          class="clamp"
          :text="item.comment"
          :disabled="noteTooltipDisabled"
          placement="bottom"
          align="left"
          @pointerenter="syncNoteTooltip"
        >
          <p
            ref="note"
            class="text note"
            title=""
          >
            {{ item.comment }}
          </p>
        </UiTooltip>
      </template>
    </div>
  </CounterpartyMasterCard>
</template>

<style scoped lang="scss">
.address-card {
  min-height: calc(16px + var(--line-height-l) + 8px + 16px + var(--line-height-s) * 4);
}

.address-body {
  min-width: 0;

  &:has(.note) .address {
    -webkit-line-clamp: 2;
    line-clamp: 2;
  }

  > :last-child {
    padding-right: 32px;
  }
}

.meta,
.text {
  margin: 0;
  min-width: 0;
  font-size: var(--font-size-s);
  line-height: var(--line-height-s);
}

.meta {
  color: var(--color-gray-500);
}

.clamp {
  display: block;
  min-width: 0;
}

.text {
  display: -webkit-box;
  overflow: hidden;
  color: var(--color-gray-900);
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: pre-wrap;
  -webkit-box-orient: vertical;
  pointer-events: none;
}

.address {
  -webkit-line-clamp: 4;
  line-clamp: 4;
}

.note {
  -webkit-line-clamp: 1;
  line-clamp: 1;
}
</style>
